import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import { css, cx } from '@wp-g2/styles';
import { memoize } from '@wp-g2/utils';
import { kebabCase, uniq } from 'lodash';
import { CONNECTED_NAMESPACE } from './constants';
import { useComponentsContext } from './context-system-provider';
import { ns } from './utils';
/**
 * @template TProps
 * @typedef {TProps & { [CONNECTED_NAMESPACE]: boolean; className: string; children?: import('react').ReactNode }} ConnectedProps
 */

/**
 * Custom hook that derives registered props from the Context system.
 * These derived props are then consolidated with incoming component props.
 *
 * @template {{ className?: string }} P
 * @param {P} props Incoming props from the component.
 * @param {string} namespace The namespace to register and to derive context props from.
 * @return {ConnectedProps<P>}
 */

export function useContextSystem(props, namespace) {
  var _finalComponentProps;

  var contextSystemProps = useComponentsContext();
  var displayName = Array.isArray(namespace) ? namespace[0] : namespace;
  var contextProps = (contextSystemProps == null ? void 0 : contextSystemProps[displayName]) || {};
  /** @type {ConnectedProps<P>} */
  // @ts-ignore We fill in the missing properties below

  var finalComponentProps = (_finalComponentProps = {}, _finalComponentProps[CONNECTED_NAMESPACE] = true, _finalComponentProps);
  var nextNs = ns(displayName);

  for (var k in nextNs) {
    if (typeof nextNs[k] === 'string') {
      finalComponentProps[k] = nextNs[k];
    }
  }

  var overrideProps = contextProps._overrides,
      contextCSS = contextProps.css,
      otherContextProps = _objectWithoutProperties(contextProps, ["_overrides", "css"]);

  var initialMergedProps = Object.entries(otherContextProps).length ? Object.assign({}, otherContextProps, props) : props;
  var classes = cx( // Resolve custom CSS from ContextSystemProvider
  contextCSS && css(contextCSS), initialMergedProps.css && css(initialMergedProps.css), memoizedGetStyledClassNameFromKey(displayName), props.className); // Provides the ability to customize the render of the component.

  var rendered = typeof initialMergedProps.renderChildren === 'function' ? initialMergedProps.renderChildren(initialMergedProps) : initialMergedProps.children;

  for (var _k in initialMergedProps) {
    /**
     * Omitting CSS prop.
     */
    if (_k !== 'css') {
      finalComponentProps[_k] = initialMergedProps[_k];
    }
  }

  for (var _k2 in overrideProps) {
    finalComponentProps[_k2] = overrideProps[_k2];
  }

  finalComponentProps.children = rendered;
  finalComponentProps.className = classes;
  return finalComponentProps;
}
/**
 * Generates the connected component CSS className based on the namespace.
 *
 * @param {string} displayName The name of the connected component.
 * @returns {string} The generated CSS className.
 */

function getStyledClassName(displayName) {
  if (!displayName || typeof displayName !== 'string') return '';
  var kebab = kebabCase(displayName);
  return "components-" + kebab + " wp-components-" + kebab;
}
/**
 * Generates the connected component CSS className based on the namespace.
 *
 * @param {string} key The name of the connected component.
 * @returns {string} The generated CSS className.
 */


function getStyledClassNameFromKey(key) {
  if (!key) return '';

  if (Array.isArray(key)) {
    return cx(uniq(key).map(getStyledClassName));
  }

  if (typeof key === 'string') {
    return getStyledClassName(key);
  }

  return '';
}

var memoizedGetStyledClassNameFromKey = memoize(getStyledClassNameFromKey);